import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type WorkspaceAgentComposerAssetPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentComposerPills';
import { useWorkspaceAgentClipsForClipIds } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentClipsForClipIds';
import { usePrivateWorkspaceAgentPanelComposerPills } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type WorkspaceAgentGuidedVariantImageSubject } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantImagePreviewSelection';
import { selectedClipItemsSelector } from '~/store/selectedItems/selectors';

const toGuidedVariantClipSubject = (
  subject: Pick<WorkspaceAgentGuidedVariantImageSubject, 'ext' | 'height' | 'thumbnailUrl' | 'type' | 'width'>,
): WorkspaceAgentGuidedVariantImageSubject => ({
  ext: subject.ext,
  height: subject.height,
  thumbnailUrl: subject.thumbnailUrl,
  type: subject.type,
  width: subject.width,
});

export const usePrivateWorkspaceAgentGuidedVariantClipSubjects = () => {
  const { scopePills } = usePrivateWorkspaceAgentPanelComposerPills();
  const selectedClipItems = useSelector(selectedClipItemsSelector);

  const assetPills = useMemo(
    () => scopePills.filter((pill): pill is WorkspaceAgentComposerAssetPill => pill.type === 'asset'),
    [scopePills],
  );
  const assetClipIds = useMemo(() => assetPills.map((pill) => pill.versionId), [assetPills]);
  const scopeClips = useWorkspaceAgentClipsForClipIds(assetClipIds);

  const clipSubjects = useMemo(() => {
    if (selectedClipItems.length > 0) {
      return selectedClipItems.map((selected) =>
        toGuidedVariantClipSubject({
          ext: selected.item.ext,
          height: selected.item.height,
          thumbnailUrl: selected.item.assets?.image || null,
          type: selected.item.type,
          width: selected.item.width,
        }),
      );
    }

    const clipsById = new Map(scopeClips.map((clip) => [clip.id, clip]));

    return assetPills.flatMap((pill) => {
      const clip = clipsById.get(pill.versionId);

      if (!clip) {
        return [];
      }

      return [
        toGuidedVariantClipSubject({
          ext: clip.ext,
          height: clip.height,
          thumbnailUrl: clip.assets?.image || pill.thumbnailUrl,
          type: clip.type,
          width: clip.width,
        }),
      ];
    });
  }, [assetPills, scopeClips, selectedClipItems]);

  return { clipSubjects };
};
